/**
 * Formulaire de creation de ticket
 * Design System v2 (Volvo-inspired) - Matching CreditNotes style
 */

import { useState } from 'react';
import { ArrowLeft, Send, HelpCircle, Headphones } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCreateTicket } from '../hooks/useTickets';
import type { TicketPriority, TicketCategory } from '../types/tickets.types';

interface TicketCreateFormProps {
  onBack?: () => void;
  onSuccess?: (ticketId: string) => void;
}

export function TicketCreateForm({ onBack, onSuccess }: TicketCreateFormProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    priority: 'MEDIUM' as TicketPriority,
    category: 'QUESTION' as TicketCategory,
  });

  const createTicket = useCreateTicket();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.subject.trim() || !formData.description.trim()) {
      return;
    }

    const result = await createTicket.mutateAsync({
      subject: formData.subject,
      description: formData.description,
      priority: formData.priority,
      category: formData.category,
    });

    onSuccess?.(result.id);
  };

  return (
    <div className="px-8 pt-8 pb-12">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={onBack}
            className="p-2.5 bg-white/40 border border-white/50 rounded-xl text-gray-600
                       hover:bg-white hover:text-teal-600 hover:border-teal-200 hover:shadow-lg hover:shadow-teal-100/50
                       active:scale-95 transition-all duration-300"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-teal-100 rounded-2xl">
              <Headphones className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('support.create.title')}</h1>
              <p className="text-gray-500 text-sm">{t('support.create.subtitle')}</p>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <form
          onSubmit={handleSubmit}
          className="bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30 p-6 space-y-6"
        >
          {/* Subject */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {t('support.create.subject')}
            </label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder={t('support.create.subjectPlaceholder')}
              className="w-full px-4 py-3 bg-white/40 border border-white/50 rounded-xl
                         focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white
                         hover:bg-white/60 hover:border-teal-200 transition-all duration-300"
              required
            />
          </div>

          {/* Category & Priority */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {t('support.create.category')}
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as TicketCategory })}
                className="w-full px-4 py-3 bg-white/40 border border-white/50 rounded-xl
                           focus:ring-2 focus:ring-teal-500 hover:bg-white/60 hover:border-teal-200
                           transition-all duration-300 cursor-pointer"
              >
                <option value="QUESTION">{t('support.categories.QUESTION')}</option>
                <option value="BUG">{t('support.categories.BUG')}</option>
                <option value="FEATURE_REQUEST">{t('support.categories.FEATURE_REQUEST')}</option>
                <option value="BILLING">{t('support.categories.BILLING')}</option>
                <option value="TECHNICAL">{t('support.categories.TECHNICAL')}</option>
                <option value="OTHER">{t('support.categories.OTHER')}</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {t('support.create.priority')}
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as TicketPriority })}
                className="w-full px-4 py-3 bg-white/40 border border-white/50 rounded-xl
                           focus:ring-2 focus:ring-teal-500 hover:bg-white/60 hover:border-teal-200
                           transition-all duration-300 cursor-pointer"
              >
                <option value="LOW">{t('support.priorities.LOW')}</option>
                <option value="MEDIUM">{t('support.priorities.MEDIUM')}</option>
                <option value="HIGH">{t('support.priorities.HIGH')}</option>
                <option value="URGENT">{t('support.priorities.URGENT')}</option>
              </select>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {t('support.create.description')}
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={t('support.create.descriptionPlaceholder')}
              className="w-full px-4 py-3 bg-white/40 border border-white/50 rounded-xl
                         focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white
                         hover:bg-white/60 hover:border-teal-200 transition-all duration-300 resize-none"
              rows={6}
              required
            />
            <p className="text-xs text-gray-500">
              {t('support.create.descriptionHelp')}
            </p>
          </div>

          {/* Help Tip */}
          <div className="bg-teal-50/50 border border-teal-100 rounded-2xl p-4 flex gap-3">
            <HelpCircle className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-teal-800">
              <p className="font-medium">{t('support.create.tip')}</p>
              <p className="text-teal-700 mt-1">
                {t('support.create.tipText')}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-white/30">
            <button
              type="button"
              onClick={onBack}
              className="px-5 py-2.5 bg-white/40 border border-white/50 rounded-xl text-gray-700 font-medium
                         hover:bg-white hover:border-gray-300 hover:shadow-lg hover:shadow-gray-100/50
                         active:scale-[0.98] transition-all duration-300"
            >
              {t('support.create.cancel')}
            </button>
            <button
              type="submit"
              disabled={createTicket.isPending || !formData.subject.trim() || !formData.description.trim()}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-xl font-medium
                         hover:bg-teal-700 hover:shadow-lg hover:shadow-teal-200/50 hover:scale-[1.02]
                         active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                         transition-all duration-300"
            >
              {createTicket.isPending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t('support.create.sending')}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  {t('support.create.sendTicket')}
                </>
              )}
            </button>
          </div>

          {/* Error */}
          {createTicket.isError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-2xl text-red-600 text-sm">
              {t('support.create.error')}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
